import { ChangeEvent, FC, useEffect } from 'react'
import { LngLatBounds } from 'mapbox-gl'
import {
  Controller,
  ControllerRenderProps,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import bbox from '@turf/bbox'
import { match } from 'ts-pattern'
import { Feature, Polygon } from 'geojson'
import { localizedStrings } from '@core/strings'
import { useToast } from '@core/toast'
import useConstants from '@core/providers/useConstants'
import { useUpdateProjectMapConfiguration } from '@core/react-query/features/projects/maps'
import { Project } from '@core/entities/project'
import { ProjectMap } from '@core/entities/project/ProjectMap'
import { Box, Flex, Text } from '@fundamentals'
import { TextField, Button, FormControlLabel, Switch } from '@common/material'
import {
  useProjectMapConfigurator,
  PMDrawMode,
  createMaxBounds,
} from '@modules/geolocation'

interface FormData {
  mapboxStyleUrl: string
  mapboxCenterLongitude: number | null
  mapboxCenterLatitude: number | null
  mapboxZoom: number | null
  canZoom: boolean
  maxBounds: LngLatBounds | null
  maxBoundsPolygon: Feature<Polygon> | null
}

type MapTabPanelProps = {
  project: Project
  active: boolean
  projectMapConfiguration: ProjectMap
}

export const MapTabPanel: FC<MapTabPanelProps> = ({
  project,
  active,
  projectMapConfiguration,
}) => {
  const { mapRef, drawRef, setDrawMode, changeDrawMode, selectFeatureOnMap } =
    useProjectMapConfigurator()
  const canEditProjectMap = project?.canEdit()
  const toast = useToast()
  const { constants } = useConstants()
  const { control, handleSubmit, reset, setValue, watch, getValues } =
    useForm<FormData>({
      defaultValues: {
        mapboxStyleUrl: constants.mapbox_default_style_url,
        mapboxCenterLongitude: constants.mapbox_default_center_longitude,
        mapboxCenterLatitude: constants.mapbox_default_center_latitude,
        mapboxZoom: constants.mapbox_default_zoom,
        canZoom: true,
        maxBounds: null,
        maxBoundsPolygon: null,
      },
    })

  const watchMaxBounds = watch('maxBounds')

  const updateProjectMapConfiguration = useUpdateProjectMapConfiguration({
    onSuccess: (response) => {
      const {
        maxBounds: projectMapMaxBounds,
        maxBoundsPolygon: projectMaxBoundsPolygon,
      } = createMaxBounds(response.maxBounds)

      reset({
        mapboxStyleUrl: response.mapBoxStyleUrl,
        mapboxCenterLongitude: response.mapCenterLongitude,
        mapboxCenterLatitude: response.mapCenterLatitude,
        mapboxZoom: response.mapZoom,
        canZoom: response.canZoom,
        maxBounds: projectMapMaxBounds,
        maxBoundsPolygon: projectMaxBoundsPolygon,
      })

      toast.success(localizedStrings.updated)
    },
  })

  const handleOnPreviewMapBoxStyleUrl = () => {
    const mapboxStyleUrl = getValues('mapboxStyleUrl')
    if (mapboxStyleUrl) {
      mapRef?.current?.getMap()?.setStyle(mapboxStyleUrl)
    }
  }

  const handleOnDrawCreate = ({ features }: { features: Array<Feature> }) => {
    const [firstFeature] = features

    if (firstFeature.geometry.type === 'Polygon') {
      const polygon = firstFeature as Feature<Polygon>

      // Delete already added polygon by MapboxDraw with auto-generated id so that we can override it with our own id
      drawRef?.current?.delete(polygon?.id?.toString())

      polygon.id = 'max-bounds-polygon'
      drawRef.current?.add(polygon)

      const [minLng, minLat, maxLng, maxLat] = bbox(polygon)
      // Create LngLatBounds using the bbox values
      const bounds = new LngLatBounds(
        [minLng, minLat], // Southwest
        [maxLng, maxLat], // Northeast
      )

      setValue('maxBounds', bounds)
      setValue('maxBoundsPolygon', polygon)
    }

    setDrawMode(PMDrawMode.SELECT_FEATURE)
    drawRef.current?.changeMode(PMDrawMode.SELECT_FEATURE, {
      onSelect: selectFeatureOnMap,
    })
  }

  const handleOnDrawUpdate = ({ features }: { features: Array<Feature> }) => {
    const maxBoundsPolygon = features?.find(
      (feature) => feature.id === 'max-bounds-polygon',
    )

    if (maxBoundsPolygon) {
      drawRef.current?.delete('max-bounds-polygon')

      const formData = getValues()

      const revertMaxBoundsPolygon = formData.maxBoundsPolygon

      drawRef.current?.add(revertMaxBoundsPolygon)

      toast.error(localizedStrings.maxBoundsPolygonCannotBeEdited)
    }
  }

  useEffect(() => {
    mapRef?.current?.on('draw.create', handleOnDrawCreate)
    mapRef?.current?.on('draw.update', handleOnDrawUpdate)

    return () => {
      mapRef?.current?.off('draw.create', handleOnDrawCreate)
      mapRef?.current?.off('draw.update', handleOnDrawUpdate)
    }
  }, [mapRef.current, drawRef.current])

  const handleOnCalibrate = () => {
    const center = mapRef.current.getCenter()

    const zoom = mapRef.current.getZoom()
    const roundedZoom = Math.round(zoom)

    setValue('mapboxCenterLongitude', center.lng)
    setValue('mapboxCenterLatitude', center.lat)
    setValue('mapboxZoom', roundedZoom)
  }

  const handleOnCanZoomChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<FormData, 'canZoom'>,
  ) => {
    const canZoom = !event.target.checked

    field.onChange(canZoom)

    if (canZoom) {
      mapRef.current.getMap().scrollZoom.enable()
      mapRef.current.getMap().boxZoom.enable()
      mapRef.current.getMap().doubleClickZoom.enable()
    } else {
      mapRef.current.getMap().scrollZoom.disable()
      mapRef.current.getMap().boxZoom.disable()
      mapRef.current.getMap().doubleClickZoom.disable()
    }
  }

  const handleOnSetMaxMapBounds = () => {
    changeDrawMode(PMDrawMode.DRAW_RECTANGLE)
  }

  const handleOnRemoveMaxMapBounds = () => {
    setValue('maxBounds', null)
    setValue('maxBoundsPolygon', null)

    drawRef.current?.delete('max-bounds-polygon')
  }

  useEffect(() => {
    if (!projectMapConfiguration) return

    const {
      maxBounds: projectMapMaxBounds,
      maxBoundsPolygon: projectMaxBoundsPolygon,
    } = createMaxBounds(projectMapConfiguration.maxBounds)

    reset({
      mapboxStyleUrl: projectMapConfiguration.mapBoxStyleUrl,
      mapboxCenterLongitude: projectMapConfiguration.mapCenterLongitude,
      mapboxCenterLatitude: projectMapConfiguration.mapCenterLatitude,
      mapboxZoom: projectMapConfiguration.mapZoom,
      canZoom: projectMapConfiguration.canZoom,
      maxBounds: projectMapMaxBounds,
      maxBoundsPolygon: projectMaxBoundsPolygon,
    })
  }, [projectMapConfiguration, reset])

  const onSubmit: SubmitHandler<FormData> = (formData) => {
    const projectMapMaxBounds = match(formData.maxBounds)
      .when(
        (maxBounds) => maxBounds !== null,
        (maxBounds) => ({
          northeast: {
            latitude: maxBounds?.getNorthEast()?.lat,
            longitude: maxBounds?.getNorthEast()?.lng,
          },
          southwest: {
            latitude: maxBounds?.getSouthWest()?.lat,
            longitude: maxBounds?.getSouthWest()?.lng,
          },
        }),
      )
      .otherwise(() => null)

    updateProjectMapConfiguration.mutate({
      projectId: project.id,
      disclaimer: projectMapConfiguration?.disclaimer || '',
      disclaimerActions: projectMapConfiguration?.disclaimerActions || [],
      mapBoxStyleUrl: formData.mapboxStyleUrl,
      mapCenterLongitude: formData.mapboxCenterLongitude,
      mapCenterLatitude: formData.mapboxCenterLatitude,
      mapZoom: formData.mapboxZoom,
      canZoom: formData.canZoom,
      maxBounds: projectMapMaxBounds,
    })
  }

  return (
    <Box hidden={!active}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection='column' p={2}>
          <Flex sx={{ mb: 2 }}>
            <Controller
              control={control}
              name={'mapboxStyleUrl'}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localizedStrings.mapBoxStyleUrl}
                  fullWidth
                  sx={{ mr: 5 }}
                  slotProps={{
                    inputLabel: {
                      shrink: !!field.value,
                    },
                  }}
                />
              )}
            />

            <Button
              variant='outlined'
              color='primary'
              sx={{ width: 200 }}
              onClick={handleOnPreviewMapBoxStyleUrl}
            >
              {localizedStrings.previewMapStyle}
            </Button>
          </Flex>

          <Controller
            control={control}
            name={'mapboxCenterLongitude'}
            render={({ field }) => (
              <TextField
                {...field}
                label={localizedStrings.mapCenterLongitude}
                sx={{ mb: 2 }}
                slotProps={{
                  inputLabel: {
                    shrink: !!field.value,
                  },
                }}
              />
            )}
          />

          <Controller
            control={control}
            name={'mapboxCenterLatitude'}
            render={({ field }) => (
              <TextField
                {...field}
                label={localizedStrings.mapCenterLatitude}
                sx={{ mb: 2 }}
                slotProps={{
                  inputLabel: {
                    shrink: !!field.value,
                  },
                }}
              />
            )}
          />

          <Controller
            control={control}
            name={'mapboxZoom'}
            render={({ field }) => (
              <TextField
                {...field}
                label={localizedStrings.mapZoom}
                sx={{ mb: 2 }}
                slotProps={{
                  inputLabel: {
                    shrink: !!field.value,
                  },
                }}
              />
            )}
          />

          <Controller
            name='canZoom'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={!field.value}
                    onChange={(e) => handleOnCanZoomChange(e, field)}
                  />
                }
                labelPlacement='start'
                label='Lock Zoom'
                sx={{ ml: 0, justifyContent: 'flex-end' }}
              />
            )}
          />

          <Flex
            flex={1}
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Text>
              {localizedStrings.moveTheMapAndPressButtonWhenHappyWithMaxBounds}
            </Text>

            {!watchMaxBounds && (
              <Button
                color='primary'
                variant='outlined'
                onClick={handleOnSetMaxMapBounds}
                sx={{ m: 2 }}
              >
                {localizedStrings.drawMaxBounds}
              </Button>
            )}

            {watchMaxBounds && (
              <Button
                color='error'
                variant='outlined'
                onClick={handleOnRemoveMaxMapBounds}
                sx={{ m: 1 }}
              >
                {localizedStrings.removeMaxBounds}
              </Button>
            )}
          </Flex>

          <Flex
            flex={1}
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Text>{localizedStrings.calibrateMapInstructions}</Text>

            <Button
              color='primary'
              variant='outlined'
              onClick={handleOnCalibrate}
            >
              {localizedStrings.calibrate}
            </Button>
          </Flex>

          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={!canEditProjectMap}
          >
            {localizedStrings.submit}
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
